import React, {useRef, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import css from './InfoMessage.css';
import Icon from 'SF/MaterialIcon';

const THRESHOLD_HEIGHT = 110;

/**
 * Info message banner for system messages.
 *
 * @param {string} message - The main content of the component
 * @param {string} icon - The material icon to use on the left side
 * @param {boolean} [isHtml=false] - Indicates that the message contains html to be rendered
 * @param {boolean} [isCollapsable=false] - Indicates that the message can be collapsable if too long
 * @returns {React.Component} A react component containing an info message
 * @constructor
 */
const InfoMessage = ({isHtml, isCollapsable, icon, message}) => {
  const ref = useRef();
  const [initialMessageHeight, setInitialMessageHeight] = useState(0);
  const [messageOpen, setMessageOpen] = useState(false);

  useEffect(() => ref.current && setInitialMessageHeight(ref.current.offsetHeight), []);

  const onToggle = (() => {
    setMessageOpen(!messageOpen);
  });
  const isCollapsed = (() => isCollapsable && !messageOpen && initialMessageHeight > THRESHOLD_HEIGHT);

  const messageClasses = [
    css.container,
    ...(isCollapsed() ? [css.limitContent] : [])
  ].join(" ");
  const textClasses = [
    css['info-text'],
    ...(isHtml ? [css['regular-weight']] : [])
  ].join(" ");

  return (
    <div ref={ref} className={messageClasses}>
      <div className={css['info-container']}>
        <div><Icon aria-hidden="true" icon={icon}/></div>
        <div className={textClasses} data-role="alertAccessMessage">
          {isHtml ? <div dangerouslySetInnerHTML={{__html: message}} /> : message}
        </div>
      </div>
      { isCollapsable && initialMessageHeight > THRESHOLD_HEIGHT &&
        <button
          aria-label="Message toggle button"
          aria-expanded={messageOpen}
          className={css.button}
          onClick={() => onToggle()}>
          <Icon
            className={css.arrow}
            aria-hidden="true"
            icon={messageOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}/>
        </button>
      }
      <div className={isCollapsed() ? css.gradient : css.hidden} />
    </div>
    );
};

InfoMessage.defaultProps = {
  icon: "visibility",
  isCollapsable: false
};

InfoMessage.propTypes = {
  isHtml: PropTypes.bool,
  isCollapsable: PropTypes.bool,
  icon: PropTypes.string,
  message: PropTypes.string.isRequired
};

export default InfoMessage;
